import React, { Fragment, FunctionComponent, useState } from 'react';
import { EditOutlined } from '@mui/icons-material';
import { Typography } from '@mui/material';
import i18next from 'i18next';

import { history } from 'helpers/history';

import SelectInput from 'components/inputs/SelectInput';

import {
  ViewContainer,
  ViewContent,
  TitleContainer,
  Title,
  TableContainer,
  CustomIconButton,
  FilePreview,
} from 'styles/view';
import { isClienteRole } from 'helpers/roles';
import useTypedSelector from 'hooks/useTypedSelector';
import { userSelector } from 'redux/selectors/auth.selector';

import { IDivorcesShowProps } from './types';
import './i18n';

const DivorcesShow: FunctionComponent<IDivorcesShowProps> = (props: IDivorcesShowProps) => {
  const { item, showTitle = true } = props;
  const [selectedFile, setSelectedFile] = useState<string | string[]>('');

  const file = item?.files?.find(({ id }) => id.toString() === selectedFile);
  const user = useTypedSelector(userSelector);

  return (
    <ViewContainer auth data-testid="divorce-show">
      <ViewContent auth>
        {showTitle && (
          <TitleContainer>
            <Title style={{ flex: 1, textAlign: 'center' }}>
              {i18next.t<string>('DIVORCES_SHOW:TITLE')}
            </Title>
            {(!isClienteRole(user) || item?.state === 'OPENED') && (
              <CustomIconButton
                onClick={() => history.push(`/public/procedures/forms/divorces/${item?.id}/edit`)}
              >
                <EditOutlined />
              </CustomIconButton>
            )}
          </TitleContainer>
        )}
        {item && (
          <>
            <Typography variant="h6">
              {i18next.t<string>('DIVORCES_SHOW:MARRIAGE_INFORMATION')}
            </Typography>
            <br />
            <TableContainer>
              <tbody>
                <tr>
                  <td>{i18next.t<string>('DIVORCES_SHOW:WEDDING_TEMPLE')}</td>
                  <td>{item.weddingTemple}</td>
                </tr>
                <tr>
                  <td>{i18next.t<string>('DIVORCES_SHOW:WEDDING_DATE')}</td>
                  <td>{item.weddingDate}</td>
                </tr>
                {item.ketubaFile && (
                  <tr>
                    <td>{i18next.t<string>('DIVORCES_SHOW:KETUBA_FILE')}</td>
                    <td>
                      <FilePreview src={item.ketubaFile} title="ketubaFile" />
                    </td>
                  </tr>
                )}
                {item.marriageCertificateFile && (
                  <tr>
                    <td>{i18next.t<string>('DIVORCES_SHOW:MARRIAGE_CERTIFICATE_FILE')}</td>
                    <td>
                      <FilePreview
                        src={item.marriageCertificateFile}
                        title="marriageCertificateFile"
                      />
                    </td>
                  </tr>
                )}
                {item.divorceCertificateFile && (
                  <tr>
                    <td>{i18next.t<string>('DIVORCES_SHOW:DIVORCE_CERTIFICATE_FILE')}</td>
                    <td>
                      <FilePreview
                        src={item.divorceCertificateFile}
                        title="divorceCertificateFile"
                      />
                    </td>
                  </tr>
                )}
              </tbody>
            </TableContainer>
            {['husband', 'wife'].map((value) => {
              const person = item[value as 'husband' | 'wife'];
              return (
                <Fragment key={`${value}-information`}>
                  <Typography variant="h6">
                    {i18next.t<string>(`DIVORCES_SHOW:${value.toUpperCase()}_INFORMATION`)}
                  </Typography>
                  <br />
                  <TableContainer>
                    <tbody>
                      <tr>
                        <td>{i18next.t<string>('DIVORCES_SHOW:LAST_NAME')}</td>
                        <td>{person.lastName}</td>
                      </tr>
                      <tr>
                        <td>{i18next.t<string>('DIVORCES_SHOW:FIRST_NAME')}</td>
                        <td>{person.firstName}</td>
                      </tr>
                      <tr>
                        <td>{i18next.t<string>('DIVORCES_SHOW:ADDRESS')}</td>
                        <td>{person.address}</td>
                      </tr>
                      <tr>
                        <td>{i18next.t<string>('DIVORCES_SHOW:APARTMENT')}</td>
                        <td>{person.apartment}</td>
                      </tr>
                      <tr>
                        <td>{i18next.t<string>('DIVORCES_SHOW:LOCALITY')}</td>
                        <td>{person.locality}</td>
                      </tr>
                      <tr>
                        <td>{i18next.t<string>('DIVORCES_SHOW:ID_CARD_TYPE')}</td>
                        <td>{person.idCardTypeName}</td>
                      </tr>
                      <tr>
                        <td>{i18next.t<string>('DIVORCES_SHOW:ID_CARD')}</td>
                        <td>{person.idCard}</td>
                      </tr>
                      {person.idCardFile && (
                        <tr>
                          <td>{i18next.t<string>('DIVORCES_FORM:ID_CARD_FILE')}</td>
                          <td>
                            <FilePreview src={person.idCardFile} title="idCardFile" />
                          </td>
                        </tr>
                      )}
                      {person.idCardBackFile && (
                        <tr>
                          <td>{i18next.t<string>('DIVORCES_FORM:ID_CARD_BACK_FILE')}</td>
                          <td>
                            <FilePreview src={person.idCardBackFile} title="idCardBackFile" />
                          </td>
                        </tr>
                      )}
                      <tr>
                        <td>{i18next.t<string>('DIVORCES_SHOW:PHONE')}</td>
                        <td>{`${person.areaCode ? `(${person.areaCode}) ` : ''}${
                          person.phone
                        }`}</td>
                      </tr>
                      <tr>
                        <td>{i18next.t<string>('DIVORCES_SHOW:EMAIL')}</td>
                        <td>{person.email}</td>
                      </tr>
                      {person.ketubaFile && (
                        <tr>
                          <td>{i18next.t<string>('DIVORCES_SHOW:KETUBA_FILE')}</td>
                          <td>
                            <FilePreview src={person.ketubaFile} title="ketubaFile" />
                          </td>
                        </tr>
                      )}
                      {person.marriageCertificateFile && (
                        <tr>
                          <td>{i18next.t<string>('DIVORCES_SHOW:MARRIAGE_CERTIFICATE_FILE')}</td>
                          <td>
                            <FilePreview
                              src={person.marriageCertificateFile}
                              title="marriageCertificateFile"
                            />
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </TableContainer>
                </Fragment>
              );
            })}
            {!!item.files?.length && (
              <>
                <Typography variant="h6">
                  {i18next.t<string>('DIVORCES_SHOW:DOCUMENTS_INFORMATION')}
                </Typography>
                <br />
                <SelectInput
                  input={{
                    name: 'files',
                    onChange: setSelectedFile,
                    value: selectedFile,
                  }}
                  label={i18next.t<string>('DIVORCES_SHOW:FILES')}
                  meta={{}}
                  options={item.files.map(({ id, name }) => ({
                    label: name,
                    value: id.toString(),
                  }))}
                />
                {file && (
                  <>
                    <FilePreview src={file.file} title="file" />
                    <br />
                  </>
                )}
              </>
            )}
            {item.comments && (
              <>
                <Typography variant="h6">{i18next.t<string>('DIVORCES_SHOW:COMMENTS')}</Typography>
                <br />
                {item.comments}
              </>
            )}
          </>
        )}
      </ViewContent>
    </ViewContainer>
  );
};

export default DivorcesShow;
